<template>
  <div class="d-inline-block">
    <b-dropdown
      text="Export"
      variant="outline-primary"
      size="sm"
      :disabled="disabled"
    >
      <b-dropdown-item
        @click="downloadImage(formats.PNG)"
      >
        PNG
      </b-dropdown-item>
      <b-dropdown-item
        @click="downloadImage(formats.JPG)"
      >
        JPG
      </b-dropdown-item>
      <b-dropdown-item
        @click="downloadImage(formats.TIFF)"
      >
        TIFF
      </b-dropdown-item>
      <b-dropdown-item
        @click="downloadClippedTiff()"
      >
        Clipped TIFF
      </b-dropdown-item>
      <b-dropdown-item
        @click="downloadClippedTiff(true)"
      >
        Clipped Product TIFF
      </b-dropdown-item>
      <b-dropdown-item
        @click="downloadPDF()"
      >
        PDF
      </b-dropdown-item>
      <b-dropdown-item
        @click="saveToProducts()"
      >
        To Products
      </b-dropdown-item>
      <b-dropdown-item
        v-b-modal.select-folder-modal
        @click="onToFileBrowserClick"
      >
        To File Browser
      </b-dropdown-item>
      <b-dropdown-item
        v-if="bynderAvailable && isPublishToBynder"
        @click="publishToBynder()"
      >
        To Bynder
      </b-dropdown-item>
    </b-dropdown>

    <div>
      <b-modal
        id="svg_file_export_modal"
        :title="modalTitle"
        centered
        @shown="handleModalShown"
        @hide="close"
      >
        <div class="f-name">
          <b-form-input
            v-model="fileName"
            type="text"
            placeholder="File name"
          />
          <b-button 
            v-b-tooltip.hover
            variant="light"
            class="icon-btn"
            title="Paste from clipboard"
            @click="onPasteFromClipboard"
          >
            <b-icon
              icon="clipboard"
              class="icon-clipboard"
            />
          </b-button>
        </div>
        
        <div
          v-if="isExportToFileBrowser"
          class="mt-3"
        >
          <select
            id="file-format"
            class="form-control form-control-sm"
            :value="selectedFormat"
            @input="setExportFormat"
          >
            <option
              v-for="(format, index) in formats"
              :key="index"
              :value="format"
            >
              {{ format.toUpperCase() }}
            </option>
          </select>
        </div>

        <template #modal-footer>
          <div
            v-if="showSelectedExport"
            class="d-flex flex-wrap mb-2 w-100"
          >
            <div class="compress-switch-wrapper d-flex align-items-center">
              <b-form-checkbox
                id="exportSelected"
                v-model="isExportSelectedChecked"
                :unchecked-value="false"
                switch
                size="lg"
                @change="(payload) => setIsExportSelected(payload)"
              />
              <label
                class="align-self-center compress-switch-label"
                for="compressTiffFile"
              >
                Export selected
              </label>
            </div>
          </div>
          
          <div
            v-if="isClippedTiffExport"
            class="d-flex flex-wrap mb-2 w-100"
          >
            <div class="compress-switch-wrapper d-flex align-items-center">
              <b-form-checkbox
                id="removeShadow"
                v-model="isRemoveShadowChecked"
                :unchecked-value="false"
                switch
                size="lg"
                @change="(payload) => setIsRemoveShadow(payload)"
              />
              <label
                class="align-self-center compress-switch-label"
                for="removeShadow"
              >
                Remove shadow
              </label>
            </div>
          </div>
          
          <div
            v-if="isClippedTiffExport"
            class="d-flex flex-wrap mb-2 w-100"
          >
            <div class="compress-switch-wrapper d-flex align-items-center">
              <b-form-checkbox
                id="compressTiffFile"
                v-model="isCompressTiffChecked"
                :unchecked-value="false"
                switch
                size="lg"
                @change="(payload) => setIsCompressTiffFile(payload)"
              />
              <label
                class="align-self-center compress-switch-label"
                for="compressTiffFile"
              >
                Compress file
              </label>
            </div>
          </div>
          
          <div 
            class="radio-tiff mb-2 w-100"
          >
            <b-form-group v-slot="{ ariaDescribedby }">
              <b-form-radio-group
                v-if="isTiffExport"
                id="radio-group-tiff"
                v-model="tiffFormat"
                :aria-describedby="ariaDescribedby"
                name="select_tiff_format"
              >
                <span class="radio-title">File extension: </span>
                <b-form-radio
                  value="tiff"
                >
                  TIFF
                </b-form-radio>
                <b-form-radio
                  value="tif"
                >
                  TIF
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </div>
          
          <div>
            <button
              class="btn btn-secondary"
              @click="close"
            >
              Cancel
            </button>
            <button
              class="btn btn-primary"
              @click="handleSave"
            >
              Save
            </button>
          </div>
        </template>
      </b-modal>
      
      <SelectFolderModal 
        @onSelectedItems="selectFolder"
      />

      <BynderPublishModal
        :canvas="canvas"
      />
    </div>
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import SelectFolderModal from '@frontend/components/modules/file-browser/modals/SelectFolderModal.vue';
import { mapActions, mapGetters } from 'vuex';
import BynderPublishModal from '@frontend/components/bynder/BynderPublishModal.vue';

export default {
  name: 'DownloadBtn',
  components: { SelectFolderModal, BynderPublishModal },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    canvas: {
      type: Object,
      default: null,
    }
  },
  
  data: () => ({
    fileName: '',
    formats: {
      PNG: 'png',
      TIFF: 'tiff',
      JPG: 'jpg',
    },
    selectedFormat: 'png',
    selectedFolder: null,
    saveFunction: null,
    modalTitle: '',
    tiffFormat: 'tiff',
    isTiffExport: false,
    showImageExport: false,
    pendingExportLog: null,
  }),

  computed: {
    ...mapGetters('svgEditor', [
      'isCompressTiffFile',
      'isClippedTiffExport',
      'isRemoveShadow',
      'isExportSelected',
    ]),

    ...mapGetters('fileBrowser', ['bynderAvailable', 'isPublishToBynder']),

    showSelectedExport() {
      return this.showImageExport;
    },
    
    isExportToFileBrowser() {
      return this.modalTitle === 'Export to File Browser';
    },

    isCompressTiffChecked: {
      get(){
        return this.isCompressTiffFile
      },
      set(newValue){
        return newValue
      }
    },

    isExportSelectedChecked: {
      get(){
        return this.isExportSelected
      },
      set(newValue){
        return newValue
      }
    },

    isRemoveShadowChecked: {
      get(){
        return this.isRemoveShadow
      },
      set(newValue){
        return newValue
      }
    },
  },

  methods: {
    ...mapActions('svgEditor', [
      'setIsProductImage',
      'setIsCompressTiffFile',
      'setIsClippedTiffExport',
      'setIsRemoveShadow',
      'setIsExportSelected',
    ]),
    
    downloadImage(format) {
      this.pendingExportLog = { exportType: `Export ${format.toUpperCase()}`, format };

      this.showImageExport = true;
      this.modalTitle = `Export ${format.toUpperCase()}`;
      this.setIsTiffExport(false);
      
      if (format === this.formats.TIFF) {
        this.setIsTiffExport(true);

        this.saveFunction = (format, fileName) => { this.canvas.downloadImage(format, fileName) };
        return this.show();
      }
      
      this.saveFunction = (fileName) => { this.canvas.downloadImage(format, fileName) };
      this.show();
    },

    downloadClippedTiff(product = false) {
      this.pendingExportLog = {
        exportType: product ? 'Export Clipped Product Image TIFF' : 'Export Clipped TIFF',
        format: this.formats.TIFF,
      };

      this.showImageExport = false;
      this.setIsTiffExport(true);
      this.setIsClippedTiffExport(true);
      this.setIsCompressTiffFile(false);
      this.setIsRemoveShadow(false);
      this.setIsProductImage(product);
      this.modalTitle = product ? 'Export Clipped Product Image TIFF' : 'Export Clipped TIFF';
      this.saveFunction = (format, fileName) => { this.canvas.downloadClippedTiff(format, fileName) };
      this.show();
    },

    downloadPDF() {
      this.pendingExportLog = { exportType: 'Export PDF', format: 'pdf' };

      this.showImageExport = false;
      this.modalTitle = 'Export PDF';
      this.saveFunction = (fileName) => { this.canvas.downloadPDF(fileName) };
      this.show();
    },

    saveToProducts() {
      this.pendingExportLog = { exportType: 'Export to Products', format: this.selectedFormat };

      this.showImageExport = true;
      this.modalTitle = 'Export to Products';
      this.saveFunction = (fileName) => { this.canvas.saveTo(fileName) };
      this.show();
    },

    onToFileBrowserClick() {
      this.pendingExportLog = { exportType: 'Export to File Browser', format: this.selectedFormat };
    },

    selectFolder(folder) {
      this.showImageExport = true;
      [this.selectedFolder] = folder;
      this.modalTitle = 'Export to File Browser';
      this.saveFunction = (fileName, fileFormat, folder) => {
        this.canvas.saveToFileBrowser(fileName, fileFormat, folder)
      };
      this.show();
    },

    setExportFormat(event) {
      this.selectedFormat = event.target.value;
    },
    
    handleSave() {
      const logFormat = this.isTiffExport ? this.tiffFormat : (this.pendingExportLog?.format || this.selectedFormat);
      this.logExport({
        exportType: this.pendingExportLog?.exportType,
        format: logFormat,
      });

      if (this.isTiffExport) {
        this.saveFunction(this.tiffFormat, this.fileName);  
      } else {
        this.saveFunction(this.fileName, this.selectedFormat, this.selectedFolder);
      }
      
      this.close();
      this.$emit('load');
    },

    async logExport({ exportType, format, fileName }) {
      const resolvedFileName = fileName || this.fileName || (this.canvas && this.canvas.getDownloadFileName
        ? this.canvas.getDownloadFileName()
        : null);

      try {
        await axios.post('/svg/log-export', {
          export_type: exportType,
          format,
          file_name: resolvedFileName,
          source_page: window.location.pathname.replace(/\/+$/, ''),
        });
      } catch (err) {
        console.error('Failed to log export:', err);
      }
    },
    
    show() {
      this.tiffFormat = this.formats.TIFF;
      this.$bvModal.show('svg_file_export_modal');
    },
    
    close() {
      this.$bvModal.hide('svg_file_export_modal');
      this.setIsTiffExport(false);
      this.setIsClippedTiffExport(false);
    },

    handleModalShown() {
      if (this.canvas && this.canvas.getDownloadFileName) {
        this.fileName = this.canvas.getDownloadFileName();
      }
    },

    async onPasteFromClipboard() {
      if (!navigator.clipboard) return;
      
      try {
        this.fileName = await navigator.clipboard.readText();
      } catch (err) {
        console.error('Failed to read clipboard:', err);
      }
    },

    setIsTiffExport(value) {
      this.isTiffExport = value;
    },

    async publishToBynder() {
      this.logExport({
        exportType: 'Export to Bynder',
        format: null,
      });

      this.$bvModal.show('bynder-image-editor-publish-modal');
    },
  },
};
</script>

<style lang="scss">
#svg_file_export_modal {
  .modal-title {
    font-weight: bold;
  }
  
  .f-name {
    display: flex;
    align-items: center;
  }

  .icon-btn {
    position: relative;
    border: none;
    margin: 0 -6px 0 5px;
    width: 35px;
    height: 35px;

    &:hover {
      border: none;
      background-color: transparent;
    }

    &:disabled {
      border: none;
    }

    &:focus {
      outline: none;
      box-shadow: none;
      border: none !important;
      background-color: transparent !important;
    }

    &.active {
      background-color: #209c86 !important;
      color: white !important;
      border: 1px solid #209c86 !important;
    }
  }
  
  .icon-clipboard {
    position: absolute;
    top: 4px;
    left: 5px;
    font-size: 170% !important;
    color: #7f7f7f;
  }

  #file-format {
    height: 38px!important;
  }
  
  .modal-footer {
    justify-content: space-between !important;
    
    .radio-tiff {
      margin-left: 10px;
      
      .form-group {
        display: contents;
        
        .radio-title {
          margin-right: 5px;
        }
      }
    }
  }

  .compress-switch-wrapper {
    margin: -5px 5px 0 5px;

    .compress-switch-label {
      margin: 8px 0 0 5px;
    }
  }
}
</style>
